import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Library, Plus, Trophy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRevealOptional } from '@/contexts/RevealContext';
import { PrizeCalculator } from './PrizeCalculator';
import { BonusRow } from './BonusRow';
import { PresetPickerDialog } from './PresetPickerDialog';
import { BonusFormDialog, type BonusFormValues } from './BonusFormDialog';
import { AwardDialog } from './AwardDialog';
import { SurvivorBlock } from './SurvivorBlock';
import { fmtUSD, type LeagueBonus, type PresetBonus, type SurvivorConfig, type MockTeam } from './types';

/**
 * SAB-288 Phase 4 — Commissioner Bonuses & Survivor section, wired.
 *
 * Reads/writes against league_bonuses + league_survivor_config directly via
 * supabase-js. RLS enforces commissioner-only writes (verified Phase 1).
 *
 * award-manual-bonus is the ONLY edge-function call from this section
 * (prize-pool gate + atomic record_user_winnings RPC + activity entry).
 *
 * Layout follows TWEAK_DEFAULTS = "list". KYC warning ($5K threshold)
 * intentionally NOT rendered (TWEAK_DEFAULTS.kycWarning=false until
 * SAB-176 KYC integration ships).
 */
export interface NFLTeamLite {
  id: string;
  name: string;
  owner_id: string;
}

const TEAM_COLOR_PALETTE = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F472B6', '#94A3B8',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function teamAvatar(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned.slice(0, 2) || 'TM').toUpperCase();
}

function teamColor(id: string): string {
  return TEAM_COLOR_PALETTE[hashId(id) % TEAM_COLOR_PALETTE.length];
}

function toAwardableTeam(t: NFLTeamLite): MockTeam {
  return {
    id: t.id,
    name: t.name,
    owner: t.owner_id.slice(0, 6),
    avatar: teamAvatar(t.name),
    color: teamColor(t.id),
  };
}

/**
 * supabase.functions.invoke surfaces non-2xx responses as a
 * FunctionsHttpError on `error`. The original Response is on `.context`;
 * read its body once to recover the JSON the edge function returned.
 */
async function readEdgeFnErrorBody(
  err: unknown,
): Promise<{ status?: number; body?: any }> {
  const resp = (err as any)?.context;
  if (resp && typeof resp.json === 'function') {
    try {
      return { status: resp.status, body: await resp.clone().json() };
    } catch {
      return { status: resp.status };
    }
  }
  return {};
}

/**
 * Map the edge function's structured error responses to user-facing
 * toast copy. Two response shapes:
 *
 *  - Edge function top-level: { error: '<code>', ...extras }
 *  - RPC-rejected via award_rejected wrapper:
 *    { error: 'award_rejected', detail: { success: false, reason: '<code>' } }
 *
 * `sentry: true` flags conditions the caller should also report to error
 * tracking — orphaned wallets, internal RPC failures, malformed payloads.
 */
function describeAwardFailure(
  body: Record<string, any>,
  bonusName: string,
  weekN: number,
  amountFmt: string,
): { title: string; description: string; sentry?: boolean } {
  const reason = body?.detail?.reason ?? body?.reason;
  const error = body?.error;

  // RPC reasons (surface either inside award_rejected.detail or top-level
  // when the function ever returns 2xx with success:false).
  if (reason === 'already_awarded') {
    return {
      title: 'Already awarded',
      description: `${bonusName} has already been awarded to that team for week ${weekN}.`,
    };
  }
  if (reason === 'wallet_not_found') {
    return {
      title: 'Recipient missing wallet',
      description:
        'No per-league wallet exists for that team. Likely an orphaned membership. Flag to support.',
      sentry: true,
    };
  }
  if (reason === 'team_not_found') {
    return {
      title: 'Team not found',
      description: 'Recipient team no longer in this league. Refresh the page.',
    };
  }
  if (reason === 'bonus_not_found') {
    return {
      title: 'Bonus not found',
      description: 'This bonus no longer exists. Refresh the page.',
    };
  }
  if (reason === 'invalid_amount' || reason === 'missing_required') {
    return {
      title: 'Award failed',
      description: 'Internal error: the award request was malformed.',
      sentry: true,
    };
  }

  // Edge-function-level errors (top-level `error` field).
  if (error === 'exceeds_prize_pool') {
    return {
      title: 'Prize pool exceeded',
      description: `Awarding ${amountFmt} would push the bonus pool past the league prize pool.`,
    };
  }
  if (error === 'only_commissioner_can_award') {
    return {
      title: 'Commissioner only',
      description: 'Only the league commissioner can award bonuses.',
    };
  }
  if (error === 'bonus_not_manual') {
    return {
      title: 'Auto bonus',
      description:
        'This bonus is awarded automatically. Only commissioner-marked manual bonuses can be awarded by hand.',
    };
  }
  if (error === 'bonus_league_mismatch' || error === 'team_league_mismatch') {
    return {
      title: 'Mismatch',
      description:
        'The bonus or team does not belong to this league. Refresh the page.',
    };
  }
  if (error === 'league_not_found') {
    return { title: 'League not found', description: 'Could not find this league.' };
  }
  if (error === 'team_not_found') {
    return { title: 'Team not found', description: 'Recipient team not found.' };
  }
  if (error === 'bonus_not_found') {
    return { title: 'Bonus not found', description: 'This bonus no longer exists. Refresh the page.' };
  }
  if (error === 'rpc_failed') {
    return {
      title: 'Award failed',
      description: 'Internal error processing the award. Try again.',
      sentry: true,
    };
  }

  return {
    title: 'Award failed',
    description:
      typeof error === 'string'
        ? error
        : 'Unexpected response from the award service.',
    sentry: true,
  };
}

export function NFLBonusesSection({
  leagueId,
  prizePool,
  currentWeek,
  playoffStartWeek,
  teams,
  isCommissioner,
}: {
  leagueId: string;
  prizePool: number;
  currentWeek: number;
  playoffStartWeek: number;
  teams: NFLTeamLite[];
  isCommissioner: boolean;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  // Optional so the section still works on surfaces that haven't wrapped
  // RevealProvider (defensive — App.tsx wraps it globally as of Phase 7).
  const reveal = useRevealOptional();

  const [bonuses, setBonuses] = useState<LeagueBonus[]>([]);
  const [survivor, setSurvivor] = useState<SurvivorConfig>({
    enabled: false,
    start_week: 1,
    end_week: Math.max(1, playoffStartWeek - 1),
    bonus_amount: 100,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [presetOpen, setPresetOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LeagueBonus | null>(null);
  const [awarding, setAwarding] = useState<LeagueBonus | null>(null);

  // Track the last server-confirmed survivor state so the debounced
  // auto-save effect doesn't fire on initial load or on round-trip echo.
  const lastSavedSurvivorRef = useRef<SurvivorConfig | null>(null);

  // Regular season end week derived from the league. Spec: end_week ≤
  // playoff_start_week - 1; the validate_survivor_end_week trigger enforces
  // the same on the server.
  const regularSeasonEndWeek = Math.max(1, playoffStartWeek - 1);

  // Bonus pool projection: per-week bonuses × weeks remaining + per-season
  // / one-time face value. Manual bonuses included (each Award eats pool
  // capacity, even if firing is discretionary).
  const weeksRemaining = Math.max(0, regularSeasonEndWeek - currentWeek + 1);
  const bonusPool = useMemo(
    () =>
      bonuses.reduce(
        (sum, b) =>
          b.frequency === 'per_week'
            ? sum + b.amount * weeksRemaining
            : sum + b.amount,
        0,
      ),
    [bonuses, weeksRemaining],
  );
  const survivorAmount = survivor.enabled ? survivor.bonus_amount : 0;

  const presetKeys = bonuses
    .map((b) => b.preset_key)
    .filter((k): k is string => !!k);

  const counts = {
    weekly: bonuses.filter((b) => b.frequency === 'per_week').length,
    season: bonuses.filter((b) => b.frequency === 'per_season').length,
    manual: bonuses.filter((b) => b.trigger_type === 'manual').length,
  };

  /* ---------- Initial fetch ---------- */
  const fetchAll = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError(null);
    try {
      const [bonusRes, survRes] = await Promise.all([
        supabase
          .from('league_bonuses')
          .select(
            'id, bonus_name, description, amount, trigger_type, frequency, preset_key, is_default, icon',
          )
          .eq('league_id', leagueId)
          .order('is_default', { ascending: false })
          .order('frequency')
          .order('bonus_name'),
        supabase
          .from('league_survivor_config')
          // SAB-632 migration 20 dropped league_survivor_config.bonus_amount;
          // the survivor prize now lives only on the auto_survivor
          // league_bonuses row (award-time source of truth). Select the
          // config's timing/enabled flags here and source the amount below.
          .select('start_week, end_week, enabled')
          .eq('league_id', leagueId)
          .maybeSingle(),
      ]);

      if (bonusRes.error) throw bonusRes.error;
      if (survRes.error) throw survRes.error;

      setBonuses(
        (bonusRes.data ?? []).map((r) => ({
          id: r.id,
          bonus_name: r.bonus_name,
          description: r.description ?? '',
          amount: Number(r.amount),
          trigger_type: r.trigger_type as LeagueBonus['trigger_type'],
          frequency: r.frequency as LeagueBonus['frequency'],
          preset_key: r.preset_key,
          is_default: r.is_default,
          icon: r.icon ?? 'trophy',
        })),
      );

      // Survivor prize amount: from the auto_survivor league_bonuses row
      // (survivor's dollar amount moved there when migration 20 dropped
      // league_survivor_config.bonus_amount). Default 100 when none exists.
      const survivorAmountFromBonuses = Number(
        (bonusRes.data ?? []).find((r) => r.trigger_type === 'auto_survivor')?.amount ?? 100,
      );
      const nextSurvivor: SurvivorConfig = survRes.data
        ? {
            enabled: survRes.data.enabled,
            start_week: survRes.data.start_week,
            end_week: survRes.data.end_week,
            bonus_amount: survivorAmountFromBonuses,
          }
        : {
            enabled: false,
            start_week: 1,
            end_week: regularSeasonEndWeek,
            bonus_amount: 100,
          };
      setSurvivor(nextSurvivor);
      lastSavedSurvivorRef.current = nextSurvivor;
    } catch (e) {
      const msg = (e as Error).message ?? 'Unknown error';
      setError(msg);
      toast({
        title: 'Failed to load bonuses',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [leagueId, regularSeasonEndWeek, toast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ---------- Mutations: bonuses ---------- */

  const handleAddPreset = useCallback(
    async (p: PresetBonus) => {
      const triggerType: LeagueBonus['trigger_type'] =
        p.frequency === 'per_season' ? 'auto_season_high' : 'auto_weekly_high';
      const { error: insErr } = await supabase.from('league_bonuses').insert({
        league_id: leagueId,
        bonus_name: p.name,
        description: p.description,
        amount: p.suggested,
        trigger_type: triggerType,
        frequency: p.frequency,
        preset_key: p.preset_key,
        is_default: false,
        icon: p.icon,
        created_by: user?.id ?? null,
      });
      if (insErr) {
        toast({
          title: 'Could not add preset',
          description: insErr.message,
          variant: 'destructive',
        });
        return;
      }
      setPresetOpen(false);
      toast({
        title: 'Preset added',
        description: `${p.name} · ${fmtUSD(p.suggested)}${p.frequency === 'per_week' ? '/wk' : ''}`,
      });
      fetchAll();
    },
    [leagueId, user?.id, toast, fetchAll],
  );

  const handleSaveCustom = useCallback(
    async (values: BonusFormValues) => {
      if (editing) {
        const { error: updErr } = await supabase
          .from('league_bonuses')
          .update({
            bonus_name: values.bonus_name,
            description: values.description,
            amount: values.amount,
            frequency: values.frequency,
          })
          .eq('id', editing.id);
        if (updErr) {
          toast({
            title: 'Could not save bonus',
            description: updErr.message,
            variant: 'destructive',
          });
          return;
        }
        toast({ title: 'Bonus updated', description: values.bonus_name });
      } else {
        const { error: insErr } = await supabase.from('league_bonuses').insert({
          league_id: leagueId,
          bonus_name: values.bonus_name,
          description: values.description,
          amount: values.amount,
          trigger_type: 'manual',
          frequency: values.frequency,
          preset_key: null,
          is_default: false,
          icon: 'sparkles',
          created_by: user?.id ?? null,
        });
        if (insErr) {
          toast({
            title: 'Could not add bonus',
            description: insErr.message,
            variant: 'destructive',
          });
          return;
        }
        toast({ title: 'Custom bonus added', description: values.bonus_name });
      }
      setEditing(null);
      setFormOpen(false);
      fetchAll();
    },
    [editing, leagueId, user?.id, toast, fetchAll],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const target = bonuses.find((b) => b.id === id);
      const { error: delErr } = await supabase
        .from('league_bonuses')
        .delete()
        .eq('id', id);
      if (delErr) {
        // FK violation = bonus has awards. Surface a clean message rather
        // than the raw Postgres error text.
        const isFkBlock =
          delErr.code === '23503' ||
          /foreign key|violates/i.test(delErr.message);
        toast({
          title: isFkBlock
            ? "This bonus has awards on record"
            : 'Could not delete bonus',
          description: isFkBlock
            ? 'Edit it instead. Past awards keep historical attribution.'
            : delErr.message,
          variant: 'destructive',
        });
        return;
      }
      if (target) {
        toast({ title: 'Bonus removed', description: target.bonus_name });
      }
      fetchAll();
    },
    [bonuses, toast, fetchAll],
  );

  /* ---------- Mutations: survivor (debounced upsert) ---------- */

  // Same comparator-by-shape used by the debounced auto-save.
  const survivorChanged = (a: SurvivorConfig, b: SurvivorConfig) =>
    a.enabled !== b.enabled ||
    a.start_week !== b.start_week ||
    a.end_week !== b.end_week ||
    a.bonus_amount !== b.bonus_amount;

  const saveSurvivor = useCallback(
    async (config: SurvivorConfig) => {
      const { error: upErr } = await supabase
        .from('league_survivor_config')
        .upsert(
          {
            league_id: leagueId,
            start_week: config.start_week,
            end_week: config.end_week,
            // bonus_amount removed (SAB-632 migration 20 dropped the column);
            // the prize is persisted on the auto_survivor league_bonuses row below.
            enabled: config.enabled,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'league_id' },
        );
      if (upErr) {
        toast({
          title: 'Could not save Survivor settings',
          description: upErr.message,
          variant: 'destructive',
        });
        return false;
      }

      // Eager auto_survivor league_bonuses row.
      //
      // When Survivor is enabled, we ensure a corresponding league_bonuses
      // row exists. Keeps an audit trail even if the league dies before
      // reaching a winner; lets process-survivor-elimination always find
      // an existing row at award time (no lazy create path needed in the
      // cron — see Phase 8 process-survivor-elimination).
      //
      // Source of truth for the prize amount is league_bonuses.amount on the
      // auto_survivor row (SAB-632 migration 20 dropped the old
      // league_survivor_config.bonus_amount); award time reads it here.
      // NOT removed on disable — preserves history.
      if (config.enabled) {
        const { data: existing, error: findErr } = await supabase
          .from('league_bonuses')
          .select('id, amount')
          .eq('league_id', leagueId)
          .eq('trigger_type', 'auto_survivor')
          .maybeSingle();
        if (findErr) {
          // Don't fail the whole save just because the audit row can't
          // be ensured; the survivor config itself saved successfully.
          console.error(
            '[saveSurvivor] auto_survivor lookup failed:',
            findErr.message,
          );
        } else if (!existing) {
          const { error: insErr } = await supabase
            .from('league_bonuses')
            .insert({
              league_id: leagueId,
              bonus_name: 'Last Team Standing',
              description: 'Last team standing in Survivor Mode.',
              amount: config.bonus_amount,
              trigger_type: 'auto_survivor',
              frequency: 'one_time',
              preset_key: 'survivor_winner',
              is_default: false,
              icon: 'skull',
              created_by: user?.id ?? null,
            });
          if (insErr) {
            console.error(
              '[saveSurvivor] auto_survivor insert failed:',
              insErr.message,
            );
          }
        } else if (Number(existing.amount) !== config.bonus_amount) {
          const { error: updErr } = await supabase
            .from('league_bonuses')
            .update({ amount: config.bonus_amount })
            .eq('id', existing.id);
          if (updErr) {
            console.error(
              '[saveSurvivor] auto_survivor amount sync failed:',
              updErr.message,
            );
          }
        }
      }

      lastSavedSurvivorRef.current = config;
      return true;
    },
    [leagueId, toast, user?.id],
  );

  // Debounce auto-save on inputs. Skip if the value already matches the
  // last server-confirmed state (initial load / round-trip echo).
  useEffect(() => {
    if (!isCommissioner) return;
    const last = lastSavedSurvivorRef.current;
    if (!last) return; // initial fetch hasn't completed
    if (!survivorChanged(survivor, last)) return;
    const t = setTimeout(() => {
      saveSurvivor(survivor);
    }, 500);
    return () => clearTimeout(t);
  }, [survivor, isCommissioner, saveSurvivor]);

  /* ---------- Mutations: award (edge function) ---------- */

  const handleAward = useCallback(
    async (teamId: string) => {
      if (!awarding) return;
      const team = teams.find((t) => t.id === teamId);
      const body = {
        league_id: leagueId,
        bonus_id: awarding.id,
        recipient_team_id: teamId,
        awarded_for_week: currentWeek,
        reason: `${awarding.bonus_name} - week ${currentWeek}`,
      };
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke(
          'award-manual-bonus',
          { body },
        );

        // Non-2xx path: read the JSON the edge function returned and map
        // its `error` / `detail.reason` to a friendly toast.
        if (invokeErr) {
          const { status, body: errBody } = await readEdgeFnErrorBody(invokeErr);
          const m = describeAwardFailure(
            errBody ?? {},
            awarding.bonus_name,
            currentWeek,
            fmtUSD(awarding.amount),
          );
          toast({
            title: m.title,
            description: m.description,
            variant: 'destructive',
          });
          // TODO(SAB-288 follow-up): wire Sentry capture for `m.sentry === true`.
          // For now console.error so the existing log capture surfaces it.
          if (m.sentry) {
            console.error('[award-manual-bonus] reportable failure', {
              status,
              body: errBody,
              awardingId: awarding.id,
              recipientTeamId: teamId,
            });
          }
          return;
        }

        // Defensive 2xx-with-success-false path. The current edge fn never
        // returns this shape (every failure goes through `json(..., status>=400)`),
        // but if shape ever changes we still surface a friendly message.
        if (data && data.success === false) {
          const m = describeAwardFailure(
            data,
            awarding.bonus_name,
            currentWeek,
            fmtUSD(awarding.amount),
          );
          toast({
            title: m.title,
            description: m.description,
            variant: 'destructive',
          });
          if (m.sentry) {
            console.error('[award-manual-bonus] reportable failure (2xx)', {
              data,
              awardingId: awarding.id,
              recipientTeamId: teamId,
            });
          }
          return;
        }

        toast({
          title: 'Award sent',
          description: `${fmtUSD(awarding.amount)} to ${team?.name ?? 'team'}`,
        });

        // SAB-288 Phase 7 — fire the explosion overlay. RevealProvider
        // de-dupes by bonus_award_id (one-shot per session), so refetches
        // can't re-trigger this same award.
        if (reveal && data?.bonus_award_id) {
          reveal.dispatch({
            id: data.bonus_award_id,
            bonusName: awarding.bonus_name,
            amount: Number(awarding.amount),
            teamId: team?.id ?? teamId,
            teamName: team?.name ?? 'Team',
            reason: `${awarding.bonus_name} - week ${currentWeek}`,
            awardedForWeek: currentWeek,
          });
        }

        setAwarding(null);
        fetchAll();
      } catch (e) {
        // Network / fetch error before the function responded — usually
        // CORS, offline, or supabase-js itself blowing up. No structured
        // body to map.
        toast({
          title: 'Award failed',
          description: (e as Error)?.message ?? 'Unexpected network error',
          variant: 'destructive',
        });
      }
    },
    [awarding, currentWeek, leagueId, teams, toast, fetchAll, reveal],
  );

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-10 flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bonuses…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">
        Failed to load bonuses. {error}
      </div>
    );
  }

  const awardableTeams = teams.map(toAwardableTeam);

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-orange-500">
          Commissioner
        </div>
        <h2 className="font-bold text-2xl mt-1 tracking-tight">
          Bonuses & Survivor
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-xl leading-relaxed">
          Redistribute a slice of the prize pool to weekly achievements, season
          milestones, and a parallel Survivor side-game. All bonuses come out of
          the existing pool, no add-on funding.
        </p>
      </div>

      <PrizeCalculator
        prizePool={prizePool}
        bonusPool={bonusPool}
        survivorAmount={survivorAmount}
        weeksRemaining={weeksRemaining}
      />

      {/* Bonuses list */}
      <div>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-bold text-lg">Configured Bonuses</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {bonuses.length} active · {counts.weekly} weekly · {counts.season} seasonal · {counts.manual} manual
            </p>
          </div>
          {isCommissioner && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPresetOpen(true)}
                className="gap-1.5"
              >
                <Library className="w-3.5 h-3.5" /> Add Preset
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Custom Bonus
              </Button>
            </div>
          )}
        </div>

        {bonuses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
            No bonuses configured. {isCommissioner ? 'Add a preset or write a custom one.' : 'Check back once your commissioner sets some up.'}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {bonuses.map((b) => (
              <BonusRow
                key={b.id}
                bonus={b}
                isCommissioner={isCommissioner}
                onEdit={() => {
                  setEditing(b);
                  setFormOpen(true);
                }}
                onDelete={() => handleDelete(b.id)}
                onAward={() => setAwarding(b)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Survivor */}
      <div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-bold text-lg">Survivor Mode</h3>
          <span className="text-xs text-muted-foreground">
            Opt-in · disabled by default
          </span>
        </div>
        <SurvivorBlock
          config={survivor}
          onChange={setSurvivor}
          regularSeasonEndWeek={regularSeasonEndWeek}
          isCommissioner={isCommissioner}
        />
      </div>

      <PresetPickerDialog
        open={presetOpen}
        onOpenChange={setPresetOpen}
        onPick={handleAddPreset}
        existingPresetKeys={presetKeys}
      />
      <BonusFormDialog
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        initial={editing}
        onSave={handleSaveCustom}
      />
      <AwardDialog
        open={!!awarding}
        onOpenChange={(o) => {
          if (!o) setAwarding(null);
        }}
        bonus={awarding}
        onAward={handleAward}
        teams={awardableTeams}
      />
    </div>
  );
}
